import { useState } from 'react';
import type { FC, PropsWithChildren } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

import { queries } from '@zoopla-eng/zds-components/themes/queries';
import { Grid } from '@zoopla-eng/zds-components/grid';
import { Tabs } from '@zoopla-eng/zds-components/tabs';
import { Select } from '@zoopla-eng/zds-components/select';
import type { SelectOption } from '@zoopla-eng/zds-components/select';
import { EventNames } from '@zoopla/tracking';

import { useBreakpointListener } from '@zoopla/dom-util';

import { EstimateExplainerModal } from '../EstimateExplainer/EstimateExplainer';
import EstimateDetails from '../EstimateDetails/EstimateDetails';
import type { GraphSaleEstimate } from '../Graph/Graph';

import { formatPrice, shortenAndFormatPrice } from '../utils/utils';

import { Maybe, RentEstimate, SaleEstimate } from '../../graphql.generated';

import {
    cardStyle,
    cardWrapperStyle,
    graphWrapperStyle,
    saleEstimateGraphContainerStyle,
    saleEstimateGraphContentStyle,
    styledEstimateContainerStyle,
    timeWrapperStyle,
} from './Modal.styles.css';
import { trackingHandler } from '../tracking/estimate.tracking';

const Graph = dynamic(() => import('../Graph/Graph'), {
    ssr: false,
});

export const PROPERTY_TAB_LABEL = 'Property';
export const RENTAL_TAB_LABEL = 'Rental';
export const PROPERTY_ESTIMATE_LABEL = 'Zoopla estimate';
export const RENTAL_ESTIMATE_LABEL = 'Rental estimate';

// The id value contains an extra month, i.e., 24 = 25. This is to include the same month from 2 years ago.
export const graphFilterOptions: SelectOption[] = [
    { id: '25', text: 'Last 2 years' },
    { id: '13', text: 'Last 12 months' },
    { id: '7', text: 'Last 6 months' },
];

export type ModalProps = {
    saleEstimates: GraphSaleEstimate[];
    saleEstimate: SaleEstimate;
    rentEstimate?: Maybe<RentEstimate>;
    uprn: string;
};

const coerceString = (val?: string | string[]) => {
    return Array.isArray(val) ? val[0] : val;
};

const getFilterOptionByQueryParam = (month: string) => {
    const optionId = parseInt(month, 10) + 1;
    const optionIdStr = optionId.toString();

    return (
        graphFilterOptions.find(({ id }) => optionIdStr === id) ||
        graphFilterOptions[0]
    );
};

const Modal: FC<PropsWithChildren<ModalProps>> = ({
    saleEstimates,
    saleEstimate,
    rentEstimate,
    uprn,
}) => {
    const { asPath, query } = useRouter();

    const queryParamAsGraphMonthValue = coerceString(query['month']) || '0';

    const defaultGraphFilterOption = getFilterOptionByQueryParam(
        queryParamAsGraphMonthValue
    );

    const isLessThanLarge = useBreakpointListener(queries.L['100']);

    const [showExplainerModal, setShowExplainerModal] = useState(false);
    const [graphFilterOption, setGraphFilterOption] = useState(
        defaultGraphFilterOption
    );
    const [selectedTab, setSelectedTab] = useState('first');

    const getBaseTracking = (uprn: string) => ({
        event: 'GAEvent',
        eventCategory: 'property details',
        eventLabel: 'estimate info',
        eventValue: Number(uprn),
    });

    const handleEstimateExplainerClick = () => {
        setShowExplainerModal(true);

        trackingHandler({
            path: asPath,
            eventAction: 'click',
            eventLabel: 'estimate info',
            eventName: 'Click Estimate Info',
        });
    };

    const handleGraphFilterOptionChange = (option: SelectOption) => {
        setGraphFilterOption(option);

        trackingHandler({
            path: asPath,
            eventAction: 'select',
            eventLabel: option.text,
            eventName: 'Dropdown Select',
        });
    };

    // This prevents confusion where the graph looks like it is of rental estimates
    const shouldShowSaleEstimateGraph =
        !isLessThanLarge || selectedTab === 'first';

    const saleEstimateTabContents = (
        <EstimateDetails
            estimateLabel={PROPERTY_ESTIMATE_LABEL}
            rangeLabel="Estimate range"
            currentPrice={formatPrice(saleEstimate.currentPrice)}
            confidenceLevel={saleEstimate.confidenceLevel}
            estimateRange={`${shortenAndFormatPrice(
                saleEstimate.lowerPrice
            )} to ${shortenAndFormatPrice(saleEstimate.upperPrice)}`}
            valueChange={saleEstimate.valueChange}
            ingestedAt={saleEstimate.ingestedAt}
            onEstimateExplainerClick={handleEstimateExplainerClick}
            uprn={uprn}
        />
    );

    const rentEstimateTabContents = rentEstimate && (
        <EstimateDetails
            estimateLabel={RENTAL_ESTIMATE_LABEL}
            rangeLabel="Rental range"
            currentPrice={`${formatPrice(rentEstimate.currentPrice)}pcm`}
            estimateRange={`${formatPrice(
                rentEstimate.lowerPrice
            )} to ${formatPrice(rentEstimate.upperPrice)}`}
            onEstimateExplainerClick={handleEstimateExplainerClick}
            uprn={uprn}
        />
    );

    return (
        <>
            <div className={styledEstimateContainerStyle}>
                <Grid>
                    <div className={cardWrapperStyle}>
                        <div className={cardStyle}>
                            {rentEstimateTabContents ? (
                                <Tabs
                                    label="Estimate Details"
                                    value={selectedTab}
                                    tabs={[
                                        {
                                            value: 'first',
                                            label: PROPERTY_TAB_LABEL,
                                            component: saleEstimateTabContents,
                                        },
                                        {
                                            value: 'second',
                                            label: RENTAL_TAB_LABEL,
                                            component: rentEstimateTabContents,
                                        },
                                    ]}
                                    onValueChange={(value: string) => {
                                        setSelectedTab(value);
                                        trackingHandler({
                                            path: asPath,
                                            eventAction: 'click',
                                            eventLabel:
                                                value === 'first'
                                                    ? 'sale value tab'
                                                    : 'rent value tab',
                                            eventName:
                                                value === 'first'
                                                    ? EventNames.CLICK_PROPERTY_TAB
                                                    : EventNames.CLICK_RENTAL_TAB,
                                        });
                                    }}
                                />
                            ) : (
                                saleEstimateTabContents
                            )}
                        </div>
                    </div>
                    {shouldShowSaleEstimateGraph && (
                        <div className={saleEstimateGraphContainerStyle}>
                            <div className={saleEstimateGraphContentStyle}>
                                <div className={timeWrapperStyle}>
                                    <Select
                                        label="Time period"
                                        options={graphFilterOptions}
                                        onSelectedOptionChange={
                                            handleGraphFilterOptionChange
                                        }
                                        selectedOption={graphFilterOption}
                                    />
                                </div>
                                <div className={graphWrapperStyle}>
                                    {/** TODO: Add more unit tests for the Graph component and create a wrapper for the whole graph and dropdown **/}
                                    <Graph
                                        estimates={saleEstimates}
                                        numberOfMonths={
                                            Number(graphFilterOption.id) ||
                                            undefined
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </Grid>
            </div>

            <EstimateExplainerModal
                close={() => setShowExplainerModal(false)}
                isOpen={showExplainerModal}
                moreInfoTracking={getBaseTracking(uprn)}
            />
        </>
    );
};

export default Modal;
